import { useEffect, useState, useContext, useRef } from "react";
import { GridRowsProp, DataGrid, GridRowModel } from "@mui/x-data-grid";
import { v4 as uuidv4 } from "uuid";
import { useColumns } from "./columns";
import { Service } from "./ordersGridTypes.types";
import { useCalculateOrder } from "./utils";
import { useCreateOrder } from "../../api/useCreateOrder";
import { useUpdateOrder } from "../../api/useUpdateOrder";
import { useNavigate } from "react-router-dom";
import { RecordContext, RecordContextType } from "../../components/record/Record";
import { OrdersFooter } from "./OrdersFooter";
import { OrdersServiceSearch } from "./OrdersServiceSearch";
import { useRelatedRecords } from "../../api/useRelatedRecords";
import { convertApiItemsToGridRows } from "./utils";
import { OrderHeaderFields } from "./OrderHeaderFields";
import { setFieldsErrors } from "../../utils/setFieldsErrors";
import { Typography } from "@mui/material";
import { useStore } from "../../store/store";
import { OrderPayments } from "./payments/OrderPayments";
import { validateOrder, ValidationResult } from "./orderValidations";
import { OrderValidationDialog } from "./OrderValidationDialog";

const gridRows: GridRowsProp = [];

declare module "@mui/x-data-grid" {
    interface FooterPropsOverrides {
        orderTotal: number;
        recordContext: RecordContextType;
        type: "form" | "detail";
    }
}

export const OrderForm = () => {
    const [item, setItem] = useState<Service | null>(null);
    const [orderTotal, setOrderTotal] = useState(0);
    const recordContext = useContext(RecordContext);
    const createOrder = useCreateOrder();
    const updateOrder = useUpdateOrder(recordContext?.item);
    const navigate = useNavigate();
    const [rows, setRows] = useState(gridRows);
    const [rowsToDelete, setRowsToDelete] = useState<Array<any>>([]);
    const hasInitializedRows = useRef(false);
    const calculateOrderTotal = useCalculateOrder();
    const relatedRecords = useRelatedRecords(
        "order_items",
        "*.*,service_variant.service.*.*,service_variant.agreement.*",
        "order_id",
        recordContext?.item?.id
    );
    const entityForm = recordContext?.entityForm;

    const columns = useColumns({
        rows: rows,
        setRows: setRows,
        setRowsToDelete: setRowsToDelete,
        type: "create",
    });
    const { notifications } = useStore();
    
    const [validationDialog, setValidationDialog] = useState({
        isOpen: false,
        message: "",
        showConfirmButton: false,
    });
    const validationResolverRef = useRef<((value: boolean) => void) | null>(null);

    const handleValidationError = (result: ValidationResult): Promise<boolean> => {
        return new Promise((resolve) => {
            validationResolverRef.current = resolve;
            setValidationDialog({
                isOpen: true,
                message: result.message || "",
                showConfirmButton: result.requiresConfirmation || false,
            });
        });
    };

    const handleValidationDialogClose = () => {
        setValidationDialog((prev) => ({ ...prev, isOpen: false }));
        validationResolverRef.current?.(false);
        validationResolverRef.current = null;
    };

    const handleValidationDialogConfirm = () => {
        setValidationDialog((prev) => ({ ...prev, isOpen: false }));
        validationResolverRef.current?.(true);
        validationResolverRef.current = null;
    };

    useEffect(() => {
        if (!item) return;
        const id = uuidv4();
        setRows((oldRows) => [
            ...oldRows,
            {
                id: id,
                variantId: item.variantId,
                sku: item.sku,
                serviceName: item.serviceName,
                agreementName: item.agreementName,
                type: item.type,
                quantity: 1,
                price: item.price,
                discount: 0,
                total: item.price,
                newRow: true,
            },
        ]);

        setItem(null);
    }, [item]);

    useEffect(() => {
        if (recordContext && recordContext.type === "update") {
            if (hasInitializedRows.current) return;
            if (relatedRecords.data && relatedRecords.data.items.length > 0) {
                const items = convertApiItemsToGridRows(relatedRecords.data.items);
                setRows(items);
                hasInitializedRows.current = true;
            }
        }
    }, [relatedRecords, recordContext]);

    useEffect(() => {
        const total = calculateOrderTotal(rows);

        setOrderTotal(total);
        entityForm?.setValue("order_items", rows);
        entityForm?.setValue("order_items_to_delete", rowsToDelete);
        entityForm?.setValue("total", total);
    }, [rows, calculateOrderTotal, entityForm, rowsToDelete]);

    useEffect(() => {
        if (recordContext?.type !== "update") return;
        
        const paymentStatus = entityForm?.watch("payment_status");
        const currentPayments = entityForm?.getValues("order_payments") || [];
        const hasPaymentsInForm = currentPayments.some(
            (payment: any) => payment.order_payment_id && payment.order === recordContext?.item?.id
        );
        
        if (paymentStatus === "not_paid" && hasPaymentsInForm) {
            alert("Não pode alterar o estado para 'Não Pago' enquanto existirem pagamentos. Por favor, apague todos os pagamentos primeiro.");
            entityForm?.setValue("payment_status", "paid");
        }
    }, [entityForm?.watch("payment_status"), recordContext?.type, recordContext?.item?.id, entityForm]);

    const processRowUpdate = (newRow: GridRowModel, oldRow: any) => {
        const updatedRow = { ...newRow };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    };

    const onSubmit = async (data: any) => {
        const isValid = await validateOrder({
            data,
            orderTotal,
            orderId: recordContext?.type === "update" ? recordContext?.item?.id : undefined,
            onValidationError: handleValidationError,
        });

        if (!isValid) return;

        try {
            if (recordContext?.type === "create") {
                const createdOrder = await createOrder.mutateAsync(data);
                navigate(`/orders/${createdOrder.id}/detail`);
                entityForm?.reset();
            } else if (recordContext?.type === "update") {
                const updatedOrder = await updateOrder.mutateAsync({ ...data, order_id: recordContext?.item?.id });
                navigate(`/orders/${updatedOrder.id}/detail`);
                entityForm?.reset();
            }
        } catch (error: any) {
            const errorCode = error?.response?.data?.errors[0].extensions.code;
            const errorMessage = error?.response?.data?.errors[0].message;
            if (recordContext?.type === "create" && error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                if (
                    (errorCode === "INTERNAL_SERVER_ERROR" || errorCode === "INVALID_PAYLOAD") &&
                    !notifications.notification.isOpen
                ) {
                    notifications.setNotification({
                        isOpen: true,
                        message: `Error - ${errorMessage}`,
                        severity: "error",
                    });
                } else {
                    if (!entityForm) return;
                    setFieldsErrors(errors, entityForm);
                }
            } else {
                notifications.setNotification({
                    isOpen: true,
                    message: `Error - ${errorMessage || "Unknown error occurred"}`,
                    severity: "error",
                });
            }
        }
    };


    if (!recordContext) return null;
    return (
        <form
            id={recordContext.entity}
            onSubmit={entityForm?.handleSubmit(onSubmit)}
            style={{ backgroundColor: "#fff", width: "100%", padding: "8px" }}
        >
            {entityForm && <OrderHeaderFields entityForm={entityForm} />}
            <Typography mt={6} mb={2} variant="h5">
                Adicione serviços:
            </Typography>
            {recordContext?.type !== "detail" ? <OrdersServiceSearch setItem={setItem} recordContext={recordContext} /> : null}
            {rows.length > 0 && (
                <DataGrid
                    sx={{ mt: 5, width: "100%" }}
                    autoHeight
                    rows={rows}
                    columns={columns}
                    editMode="cell"
                    processRowUpdate={processRowUpdate}
                    slots={{
                        footer: OrdersFooter,
                    }}
                    slotProps={{
                        footer: { orderTotal, recordContext, type: "form" },
                    }}
                />
            )}
            {entityForm && (
                <OrderPayments
                    orderId={recordContext?.item?.id}
                    orderTotal={orderTotal}
                    entityForm={entityForm}
                    paymentStatus={entityForm.watch("payment_status") || ""}
                    isReadOnly={false}
                    recordType={recordContext.type}
                    existingPayments={recordContext?.item?.orders_payments || []}
                />
            )}
            <OrderValidationDialog
                isOpen={validationDialog.isOpen}
                message={validationDialog.message}
                showConfirmButton={validationDialog.showConfirmButton}
                onClose={handleValidationDialogClose}
                onConfirm={handleValidationDialogConfirm}
            />
        </form>
    );
};
